import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { scrollToTopIfSamePage } from '../utils/scrollToTop';

const CTA = ({ 
  title = "Ready to Transform Your Business with AI?",
  subtitle = "Join innovative companies who've already started their AI journey. Get your free AI audit and discover opportunities in just 30 minutes.",
  badgeText = "Limited Time",
  badgeIcon = "bi-clock",
  primaryButtonText = "Get Free AI Audit",
  secondaryButtonText = "Schedule Consultation",
  primaryButtonIcon = "bi-rocket-takeoff",
  secondaryButtonIcon = "bi-calendar-check"
}) => {
  const location = useLocation();
  return (
    <section className="cta-section py-5" style={{
      background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 30%, #334155 70%, #475569 100%)',
      position: 'relative',
      overflow: 'hidden',
      padding: '6rem 0'
    }}>
      {/* Background Pattern */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: `
          radial-gradient(circle at 20% 80%, rgba(14, 165, 233, 0.06) 0%, transparent 50%),
          radial-gradient(circle at 80% 20%, rgba(56, 189, 248, 0.04) 0%, transparent 50%)
        `,
        zIndex: 1
      }}></div>

      {/* Floating Particles */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 2,
        pointerEvents: 'none'
      }}>
        {[...Array(8)].map((_, i) => (
          <div key={i} style={{
            position: 'absolute',
            width: '6px',
            height: '6px',
            background: 'rgba(56, 189, 248, 0.4)',
            borderRadius: '50%',
            top: `${15 + i * 12}%`,
            left: `${8 + i * 15}%`,
            animation: `float ${4 + i * 0.3}s ease-in-out infinite`,
            animationDelay: `${i * 0.4}s`
          }}></div>
        ))}
      </div>

      <div className="container" style={{ position: 'relative', zIndex: 3 }}>
        <div className="row justify-content-center text-center">
          <div className="col-lg-8">


            <h2 style={{
              fontSize: 'clamp(2.5rem, 5vw, 3.5rem)',
              fontWeight: '800',
              color: '#ffffff',
              marginBottom: '1.5rem',
              lineHeight: '1.2',
              textShadow: '0 4px 20px rgba(0, 0, 0, 0.3)'
            }}
            dangerouslySetInnerHTML={{ __html: title }}
            />
            
            <p style={{
              fontSize: '1.2rem',
              color: 'rgba(255, 255, 255, 0.9)',
              lineHeight: '1.7',
              marginBottom: '3rem',
              maxWidth: '700px',
              margin: '0 auto 3rem',
              fontWeight: '500'
            }}>
              {subtitle}
            </p>
            
            <div style={{
              display: 'flex',
              gap: 'clamp(1rem, 3vw, 1.5rem)',
              justifyContent: 'center',
              flexWrap: 'wrap',
              alignItems: 'center'
            }}
            className="cta-buttons-container">
              <Link 
                to="/contact" 
                onClick={() => scrollToTopIfSamePage(location.pathname, '/contact')}
                style={{
                  background: 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)',
                  border: 'none',
                  color: '#ffffff',
                  padding: 'clamp(1rem, 3vw, 1.4rem) clamp(2rem, 6vw, 3.5rem)',
                  borderRadius: 'clamp(40px, 8vw, 60px)',
                  fontWeight: '700',
                  fontSize: 'clamp(0.9rem, 2.5vw, 1.1rem)',
                  textDecoration: 'none',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 'clamp(0.5rem, 1.5vw, 0.75rem)',
                  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                  boxShadow: '0 10px 30px rgba(14, 165, 233, 0.4), 0 6px 20px rgba(0, 0, 0, 0.1)',
                  minWidth: 'clamp(200px, 40vw, 220px)',
                  width: 'auto',
                  maxWidth: '300px',
                  justifyContent: 'center',
                  position: 'relative',
                  overflow: 'hidden',
                  textAlign: 'center'
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = 'translateY(-5px)';
                  e.target.style.boxShadow = '0 15px 40px rgba(14, 165, 233, 0.5), 0 10px 30px rgba(0, 0, 0, 0.2)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 10px 30px rgba(14, 165, 233, 0.4), 0 6px 20px rgba(0, 0, 0, 0.1)';
                }}>
                  <i className={`bi ${primaryButtonIcon}`} style={{
                    fontSize: 'clamp(1rem, 2.5vw, 1.1rem)'
                  }}></i>
                  {primaryButtonText}
                </Link>
              
              <Link 
                to="/contact" 
                onClick={() => scrollToTopIfSamePage(location.pathname, '/contact')}
                style={{
                  background: 'rgba(255, 255, 255, 0.08)',
                  border: '2px solid rgba(14, 165, 233, 0.3)',
                  color: '#ffffff',
                  padding: 'clamp(1rem, 3vw, 1.4rem) clamp(2rem, 6vw, 3.5rem)',
                  borderRadius: 'clamp(40px, 8vw, 60px)',
                  fontWeight: '700',
                  fontSize: 'clamp(0.9rem, 2.5vw, 1.1rem)',
                  textDecoration: 'none',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 'clamp(0.5rem, 1.5vw, 0.75rem)',
                  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                  backdropFilter: 'blur(20px)',
                  minWidth: 'clamp(200px, 40vw, 220px)',
                  width: 'auto',
                  maxWidth: '300px',
                  justifyContent: 'center',
                  position: 'relative',
                  overflow: 'hidden',
                  textAlign: 'center'
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = 'translateY(-5px)';
                  e.target.style.background = 'rgba(255, 255, 255, 0.15)';
                  e.target.style.borderColor = 'rgba(14, 165, 233, 0.6)';
                  e.target.style.boxShadow = '0 15px 40px rgba(14, 165, 233, 0.2), 0 10px 30px rgba(0, 0, 0, 0.1)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.background = 'rgba(255, 255, 255, 0.08)';
                  e.target.style.borderColor = 'rgba(14, 165, 233, 0.3)';
                  e.target.style.boxShadow = 'none';
                }}>
                  <i className={`bi ${secondaryButtonIcon}`} style={{
                    fontSize: 'clamp(1rem, 2.5vw, 1.1rem)'
                  }}></i>
                  {secondaryButtonText}
                </Link>
            </div>

          </div>
        </div>
      </div>

      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-20px) rotate(180deg); }
        }
      `}</style>
    </section>
  );
};

export default CTA; 